import { useMemo, useState, useEffect } from "react";

const matchesDiscountRange = (product, range) => {
  if (!range) return true;

  const discount = Number(product.discount || 0);
  return range.min === 0
    ? discount >= range.min && discount <= range.max
    : discount > range.min && discount <= range.max;
};

export function useProductFilters(products) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [maxPrice, setMaxPrice] = useState(0);
  const [priceLimit, setPriceLimit] = useState(0);
  const [minDiscount, setMinDiscount] = useState(0);
  const [selectedDiscountRange, setSelectedDiscountRange] = useState(null);
  const [minRating, setMinRating] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const getAverageRating = (p) => {
    const ratings = p?.rating;
    if (!ratings || !Array.isArray(ratings) || ratings.length === 0) return 0;
    const totalWeight = ratings.reduce((sum, r) => sum + (r.rating || 0) * (r.zcount || 0), 0);
    const totalCount = ratings.reduce((sum, r) => sum + (r.zcount || 0), 0);
    return totalCount > 0 ? totalWeight / totalCount : 0;
  };
  const [filteredMaxPrice, setFilteredMaxPrice] = useState(0);

  const uniqueBrands = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }
    if (selectedSubcategory.length) {
      result = result.filter((p) => selectedSubcategory.includes(p.subcategory));
    }
    if (selectedModels.length) {
      result = result.filter((p) =>
        selectedModels.includes(p.modelno || p.displaymodelno),
      );
    }
    if (minDiscount > 0) {
      result = result.filter((p) => (p.discount || 0) >= minDiscount);
    }
    result = result.filter((p) => matchesDiscountRange(p, selectedDiscountRange));
    if (minRating > 0) {
      result = result.filter((p) => getAverageRating(p) >= minRating);
    }
    result = result.filter((p) => {
      let price = p.mrp;
      if (p.discount) price = price - (price * p.discount) / 100;
      return price <= maxPrice;
    });
    return [...new Set(result.map((p) => p.brand).filter(Boolean))];
  }, [
    products,
    selectedCategory,
    selectedSubcategory,
    selectedModels,
    minDiscount,
    selectedDiscountRange,
    minRating,
    maxPrice,
  ]);

  const categories = useMemo(() => {
    let result = [...products];
    if (selectedModels.length) {
      result = result.filter((p) =>
        selectedModels.includes(p.modelno || p.displaymodelno),
      );
    }
    if (selectedBrands.length) {
      result = result.filter((p) => selectedBrands.includes(p.brand));
    }
    if (minDiscount > 0) {
      result = result.filter((p) => (p.discount || 0) >= minDiscount);
    }
    result = result.filter((p) => matchesDiscountRange(p, selectedDiscountRange));
    if (minRating > 0) {
      result = result.filter((p) => getAverageRating(p) >= minRating);
    }
    result = result.filter((p) => {
      let price = p.mrp;
      if (p.discount) price = price - (price * p.discount) / 100;
      return price <= maxPrice;
    });
    const map = {};
    result.forEach((p) => {
      if (!map[p.category]) map[p.category] = 0;
      map[p.category]++;
    });
    return Object.keys(map).map((k) => ({ name: k, count: map[k] }));
  }, [products, selectedModels, selectedBrands, minDiscount, selectedDiscountRange, minRating, maxPrice]);

  const models = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      const model = p.modelno || p.displaymodelno;
      if (!map[model]) map[model] = 0;
      map[model]++;
    });
    return Object.keys(map).map((k) => ({ name: k, count: map[k] }));
  }, [products]);

  const subcategories = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }

    const map = {};
    result.forEach((p) => {
      if (!p.subcategory) return;
      if (!map[p.subcategory]) map[p.subcategory] = 0;
      map[p.subcategory]++;
    });
    return Object.keys(map).map((k) => ({ name: k, count: map[k] }));
  }, [
    products,
    selectedCategory,
  ]);

  useEffect(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }
    if (selectedSubcategory.length) {
      result = result.filter((p) => selectedSubcategory.includes(p.subcategory));
    }
    if (selectedModels.length) {
      result = result.filter((p) =>
        selectedModels.includes(p.modelno || p.displaymodelno),
      );
    }
    if (minDiscount > 0) {
      result = result.filter((p) => (p.discount || 0) >= minDiscount);
    }
    result = result.filter((p) => matchesDiscountRange(p, selectedDiscountRange));
    if (selectedBrands.length) {
      result = result.filter((p) => selectedBrands.includes(p.brand));
    }
    if (minRating > 0) {
      result = result.filter((p) => getAverageRating(p) >= minRating);
    }
    result = result.filter((p) => {
      let price = p.mrp;
      if (p.discount) price = price - (price * p.discount) / 100;
      return price <= maxPrice;
    });
    setFilteredProducts(result);

    const filteredMax = result.length
      ? Math.max(
          ...result.map((p) => {
            let price = p.mrp;
            if (p.discount) price = price - (p.discount * price) / 100;
            return price;
          }),
        )
      : 0;
    setFilteredMaxPrice(filteredMax);
  }, [
    products,
    selectedCategory,
    selectedSubcategory,
    selectedModels,
    maxPrice,
    selectedBrands,
    minDiscount,
    selectedDiscountRange,
    minRating,
  ]);

  const displayMaxPrice = useMemo(() => {
    if (!filteredProducts.length) return 0;
    return Math.max(
      ...filteredProducts.map((p) => {
        let price = p.mrp;
        if (p.discount) price = price - (p.discount * price) / 100;
        return price;
      }),
    );
  }, [filteredProducts]);

  useEffect(() => {
    if (products.length > 0) {
      const maxMRP = Math.max(
        ...products.map((p) => Number(p.mrp || p.price || 0)),
      );
      setPriceLimit(maxMRP);
      setMaxPrice(maxMRP);
    }
  }, [products]);

  useEffect(() => {
    setSelectedBrands((prev) =>
      prev.filter((brand) => uniqueBrands.includes(brand)),
    );
  }, [uniqueBrands]);

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedSubcategory([]);
    setSelectedModels([]);
    setMaxPrice(priceLimit);
    setSelectedBrands([]);
    setMinDiscount(0);
    setSelectedDiscountRange(null);
    setMinRating(0);
  };

  return {
    selectedCategory,
    setSelectedCategory,
    selectedSubcategory,
    setSelectedSubcategory,
    selectedBrands,
    setSelectedBrands,
    selectedModels,
    setSelectedModels,
    maxPrice,
    setMaxPrice,
    priceLimit,
    setPriceLimit,
    minDiscount,
    setMinDiscount,
    selectedDiscountRange,
    setSelectedDiscountRange,
    minRating,
    setMinRating,
    filteredProducts,
    filteredMaxPrice,
    uniqueBrands,
    categories,
    subcategories,
    models,
    displayMaxPrice,
    clearFilters,
  };
}
